import type { Offset2D, IPositionFixedStickyModule } from '../types';

/**
 * Modul für Position-Fixed-Sticky-Funktionalität
 * Verwaltet Elemente mit position:fixed, die sich wie position:sticky verhalten sollen
 */
const PositionFixedStickyModule: IPositionFixedStickyModule = {
    observerInstance: null,

    /**
     * Findet das nächste dashboard-layout-body Element
     * @param element - Das Startelement
     * @returns Das gefundene dashboard-layout-body Element oder null
     */
    findDashboardLayoutBody(element: HTMLElement): HTMLElement | null {
        let currentElement: HTMLElement | null = element.parentElement;

        while (currentElement) {
            if (currentElement.classList.contains('dashboard-layout-body')) {
                return currentElement;
            }
            currentElement = currentElement.parentElement;
        }

        return null;
    },

    /**
     * Berechnet den kumulierten Scroll-Offset aller Parent-Elemente
     * @param element - Das Startelement
     * @returns Kumulierter Scroll-Offset in x und y
     */
    getCumulativeScrollOffset(element: HTMLElement): Offset2D {
        let scrollX = 0;
        let scrollY = 0;
        let currentElement: HTMLElement | null = element.parentElement;

        while (currentElement) {
            scrollX += currentElement.scrollLeft || 0;
            scrollY += currentElement.scrollTop || 0;
            currentElement = currentElement.parentElement;
        }

        return { x: scrollX, y: scrollY };
    },

    /**
     * Berechnet die Clip-Path basierend auf dem dashboard-layout-body
     * @param element - Das fixed-sticky Element
     * @param dashboardBody - Das dashboard-layout-body Element
     * @returns Die clip-path CSS-Eigenschaft
     */
    calculateClipPath(element: HTMLElement, dashboardBody: HTMLElement): string {
        const bodyRect = dashboardBody.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        // Berechne die relativen Positionen
        const top = Math.max(0, bodyRect.top - elementRect.top);
        const left = Math.max(0, bodyRect.left - elementRect.left);
        const right = Math.max(0, elementRect.right - bodyRect.right);
        const bottom = Math.max(0, elementRect.bottom - bodyRect.bottom);

        // Berechne die Dimensionen des sichtbaren Bereichs
        const clipTop = top;
        const clipLeft = left;

        return `inset(${clipTop}px ${right}px ${bottom}px ${clipLeft}px)`;
    },

    /**
     * Passt das marginTop und marginLeft aller Elemente mit data-fixed-sticky 
     * an den kumulierten Scroll-Offset aller Parent-Elemente an und wendet Clipping an
     */
    adjustFixedStickyElements(): void {
        // Finde alle Elemente mit data-fixed-sticky
        const fixedStickyElements = document.querySelectorAll<HTMLElement>('[data-fixed-sticky]');

        // Passe margin und clipping für jedes Element an
        fixedStickyElements.forEach(element => {
            const offset = this.getCumulativeScrollOffset(element);
            let topOffset = offset.y;

            // Wenn data-fixed-sticky-child-bottom vorhanden ist, 
            // verschiebe das Element um die Höhe des letzten Kindes nach oben
            if (element.hasAttribute('data-fixed-sticky-child-bottom')) {
                const lastChild = element.lastElementChild as HTMLElement;
                if (lastChild) {
                    const childHeight = lastChild.offsetHeight;
                    topOffset += childHeight;
                }
            }

            element.style.marginTop = `-${topOffset}px`;
            element.style.marginLeft = `-${offset.x}px`;

            // Finde das dashboard-layout-body und wende Clipping an
            const dashboardBody = this.findDashboardLayoutBody(element);
            if (dashboardBody) {
                const clipPath = this.calculateClipPath(element, dashboardBody);
                element.style.clipPath = clipPath;
            } else {
                // Entferne clip-path, wenn kein dashboard-layout-body gefunden wurde
                element.style.clipPath = '';
            }
        });
    },

    /**
     * Initialisiert Scroll-Listener auf allen relevanten Parent-Elementen
     */
    initializeScrollListener(): void {
        const fixedStickyElements = document.querySelectorAll<HTMLElement>('[data-fixed-sticky]');

        if (fixedStickyElements.length === 0) {
            console.warn('Keine Elemente mit data-fixed-sticky gefunden');
            return;
        }

        // Sammle alle einzigartigen scrollbaren Parent-Elemente
        const scrollableParents = new Set<HTMLElement>();

        fixedStickyElements.forEach(element => {
            let currentElement: HTMLElement | null = element.parentElement;

            while (currentElement) {
                // Pr�fe, ob das Element scrollbar ist
                const hasScrollX = currentElement.scrollWidth > currentElement.clientWidth;
                const hasScrollY = currentElement.scrollHeight > currentElement.clientHeight;

                if (hasScrollX || hasScrollY) {
                    scrollableParents.add(currentElement);
                }

                currentElement = currentElement.parentElement;
            }
        });

        // Binde adjustFixedStickyElements an this-Kontext
        const boundAdjust = this.adjustFixedStickyElements.bind(this);

        // Entferne vorhandene Listener und füge neue hinzu
        scrollableParents.forEach(parent => {
            parent.removeEventListener('scroll', boundAdjust);
            parent.addEventListener('scroll', boundAdjust);
        });

        // Füge Resize-Listener hinzu, um Clipping bei Größenänderungen anzupassen
        window.removeEventListener('resize', boundAdjust);
        window.addEventListener('resize', boundAdjust);

        // Initiale Anpassung
        this.adjustFixedStickyElements();
    },

    /**
     * Entfernt alle Scroll-Listener
     */
    removeScrollListener(): void {
        const fixedStickyElements = document.querySelectorAll<HTMLElement>('[data-fixed-sticky]');
        const boundAdjust = this.adjustFixedStickyElements.bind(this);

        fixedStickyElements.forEach(element => {
            let currentElement: HTMLElement | null = element.parentElement;

            while (currentElement) {
                currentElement.removeEventListener('scroll', boundAdjust);
                currentElement = currentElement.parentElement;
            }
        });

        window.removeEventListener('resize', boundAdjust);
    },

    /**
     * Beobachtet das DOM und initialisiert den Scroll-Listener automatisch,
     * sobald ein Element mit data-fixed-sticky erscheint
     */
    observeDashboardLayoutBody(): void {
        // Pr�fe zun�chst, ob Elemente bereits existieren
        const existingElements = document.querySelectorAll('[data-fixed-sticky]');
        if (existingElements.length > 0) {
            this.initializeScrollListener();
        }

        // Stoppe vorhandenen Observer, falls vorhanden
        if (this.observerInstance) {
            this.observerInstance.disconnect();
        }

        // Erstelle einen neuen MutationObserver
        this.observerInstance = new MutationObserver(() => {
            const fixedStickyElements = document.querySelectorAll('[data-fixed-sticky]');

            if (fixedStickyElements.length > 0) {
                // Elemente gefunden - initialisiere Listener
                this.initializeScrollListener();
            }
        });

        // Starte die Beobachtung des gesamten body-Elements
        this.observerInstance.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-fixed-sticky']
        });
    },

    /**
     * Stoppt die Beobachtung des DOM
     */
    stopObserving(): void {
        if (this.observerInstance) {
            this.observerInstance.disconnect();
            this.observerInstance = null;
        }
    }
};

// Automatisch mit Beobachtung starten
PositionFixedStickyModule.observeDashboardLayoutBody();

export default PositionFixedStickyModule;
